import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container, Box, Grid, FormGroup, FormControlLabel, Checkbox, Button
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import CheckBoxOutlineBlankOutlinedIcon from '@mui/icons-material/CheckBoxOutlineBlankOutlined';
import { useHistory } from 'react-router-dom';
import { usePDF } from '@react-pdf/renderer';
import { useStyles } from './style';
import { data as donorAggrementData } from './data';
import { AgreementBox } from '../shared/AgreementBox/AgreementBox';
import PdfAgreement from '../PdfAgreement/PdfAgreement';
import { ProfileInfoHeader } from '../shared/ProfileInfoHeader';
import { blobToBase64 } from '../../utils/generalFunc';
import { dateFormat, getcurrentTime } from '../../utils/dateFormat';
import { assignmentOfBenefitAgreementAction } from '../../redux/agreementCheckbox/Reducer';
import { useUploadDocumentMutation } from '../../redux/services/PostDocument/PostDocument';
import { useUpdateProfileMutation } from '../../redux/services/UpdateProfile/UpdateProfile';

export const AssignmentOfBenefit = () => {
  const currDate = new Date();
  const getDate = dateFormat(currDate);
  const getCurrentTime = getcurrentTime(currDate);
  const classes = useStyles();
  const history = useHistory();
  const frontCardPhoto = useSelector((state) => state.camera.frontCardImage);
  const backCardPhoto = useSelector((state) => state.camera.backCardImage);
  const profileImg = useSelector((state) => state.camera.image);
  const profile = useSelector((state) => state.profile.profilInfo);
  const [instance] = usePDF({
    document: <PdfAgreement
      name={`${profile.First} ${profile.Last}`}
      insurer={profile.Insurer}
      signDate={getDate}
      signTime={getCurrentTime}
    />
  });
  const [hasReadAgreement, setHasReadAgreement] = useState(false);
  const errorUploadinDocument = useRef(false);
  const errorUpdateProfile = useRef(false);
  const [PostDocumentApi, {
    isError, isSuccess, isLoading, reset
  }] = useUploadDocumentMutation();
  const agreementCheckbox = useSelector((state) => state.agreementCheckboxes.assignmentOfBenefitAgreement);
  const [checked, setChecked] = useState(agreementCheckbox);
  const dispatch = useDispatch();

  const [updateInfoPostApi, {
    isError: updateApiError, isSuccess: updateApiSuccess, isLoading: updateApiLoading, reset: updateApiReset
  }] = useUpdateProfileMutation();

  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    if (agreementCheckbox.userAgree && agreementCheckbox.termsAgree) {
      setHasReadAgreement(true);
    }
  }, [agreementCheckbox]);

  useEffect(() => {
    if (errorUploadinDocument.current) {
      enqueueSnackbar('something went wrong in uploading document!!', { variant: 'error' });
    }
    if (errorUpdateProfile.current) {
      enqueueSnackbar('something went wrong in updating profile!!', { variant: 'error' });
    }
    errorUploadinDocument.current = false;
    errorUpdateProfile.current = false;
  }, [errorUploadinDocument.current, errorUpdateProfile.current]);

  if (isSuccess && updateApiSuccess) {
    history.push('/all-done');
    updateApiReset();
    reset();
  }
  if (isError) {
    errorUploadinDocument.current = true;
    reset();
  }
  if (updateApiError) {
    errorUpdateProfile.current = true;
    updateApiReset();
  }

  const documentApiCalling = (documentBase64) => {
    PostDocumentApi({
      DonorId: profile.Id,
      FileContentBase64: documentBase64,
      Filename: `${profile.Id}_regdocs_${getDate}`
    });
  };

  const updateInfoApiCalling = async () => {
    const cardFront = frontCardPhoto?.split('base64,')[1];
    const cardBack = backCardPhoto?.split('base64,')[1];
    const donorPhoto = profileImg?.split('base64,')[1];
    const name = profile.SubscriberName.trim();
    let subsFirstName = '';
    let subsLastName = '';
    if (name) {
      const nameArr = name?.includes(' ') ? name?.split(' ') : name;
      subsFirstName = Array.isArray(nameArr) ? nameArr?.splice(0, (nameArr.length - 1))?.join(' ') : nameArr;
      subsLastName = Array.isArray(nameArr) ? nameArr[nameArr.length - 1] : '';
    }

    await updateInfoPostApi({
      UniqueIdentifier: profile.Id,
      InsuranceCardPhotoFront: cardFront || profile.InsuranceCardPhotoFront || null,
      InsuranceCardPhotoBack: cardBack || profile.InsuranceCardPhotoBack || null,
      DonorPhoto: donorPhoto || null,
      HomeAddressLine1: profile.HomeAddressLine1,
      HomeAddressLine2: profile.HomeAddressLine2,
      City: profile.City,
      State: profile.State,
      ZipCode: profile.ZipCode,
      SSN: profile.SSN,
      Gender: profile.Gender,
      Ethnicity: profile.Ethnicity,
      PrimaryLanguage: profile.PrimaryLanguage,
      Phone: profile.Phone,
      EnrollforSMS: profile.SMSEnrollment,
      EmailAddress: profile.EmailAddress,
      Insurer: profile.Insurer,
      CarrierPhone: profile.CarrierPhone,
      MemberID: profile.MemberID,
      Group: profile.Group,
      IsPatientSubscriber: true,
      SubscriberBirthdate: profile.SubscriberBirthdate,
      SubscriberFirstName: subsFirstName || '',
      SubscriberLastName: subsLastName || '',
      SubscriberGender: profile.SubscriberGender,
      SubscriberAddress: profile.SubscriberAddress,
      SubscriberEmployee: profile.SubscriberEmployee,
      PrimaryCarePhysician: profile.PrimaryCarePhysician,
      PrimaryCarePhone: profile.PrimaryCarePhone,
      PrimaryCareAddress: profile.PrimaryCareAddress,
      TreatmentProviderName: profile.TreatmentProviderName,
      TreatmentProviderPhone: profile.TreatmentProviderPhone,
      TreatmentProviderAddress: profile.TreatmentProviderAddress,
      TreatmentCounselorName: profile.TreatmentCounselorName,
      TreatmentCounselorPhone: profile.TreatmentCounselorPhone,
      EmergencyContactName: profile.EmergencyContactName,
      EmergencyContactAddress: profile.EmergencyContactAddress,
      EmergencyContactPhone: profile.EmergencyContactPhone,
      EmergencyContactEmail: profile.EmergencyContactEmail,
      SocialWorkerName: profile.SocialWorkerName,
      SocialWorkerPhone: profile.SocialWorkerPhone,
      VABranchServed: profile.VABranchServed,
      DischargeStatus: profile.DischargeStatus
    });
  };

  const signDocumentHandler = async () => {
    const documentBase64 = await blobToBase64(instance.blob);
    updateInfoApiCalling().then(() => {
      documentApiCalling(documentBase64);
    }).catch((err) => {
      console.log('err', err);
      enqueueSnackbar('something went wrong !!', { variant: 'error' });
    });
  };

  const onBackHandler = () => {
    history.goBack();
  };

  const AgreementCheckHandler = (event) => {
    setChecked({ ...checked, [event.target.name]: event.target.checked });
    dispatch(assignmentOfBenefitAgreementAction({ ...checked, [event.target.name]: event.target.checked }));
  };

  const onEndOfScrollFunc = () => {
    setHasReadAgreement(true);
  };

  return (
    <Container maxWidth="md">
      <ProfileInfoHeader />
      <Grid container rowSpacing={{ xs: 1, sm: 1, md: 1 }} pb={{ xs: 3, md: 1 }} justifyContent="center">
        <Grid item xs={12} sm={7} md={7}>
          <Box>
            <Box className={classes.agreementHeading}>Assignment of Benefits</Box>
          </Box>
        </Grid>
      </Grid>
      <Grid container rowSpacing={{ xs: 1, sm: 1, md: 1 }} pb={{ xs: 1, md: 1 }} justifyContent="center">
        <Grid item xs={12} sm={12} md={12}>
          <AgreementBox isIndex={false} data={donorAggrementData()} onEndOfScrollFunc={onEndOfScrollFunc} />
        </Grid>
      </Grid>
      <Grid container rowSpacing={{ xs: 1, sm: 1, md: 1 }} pb={{ xs: 3, md: 2 }} justifyContent="start">
        <Grid item xs={12} sm={12} md={12}>
          {!hasReadAgreement && <Box className={classes.checkboxErrorText}>* Please read the terms of the document to enable the checkboxes</Box>}
          <FormGroup>
            <FormControlLabel
              control={<Checkbox
                icon={<CheckBoxOutlineBlankOutlinedIcon />}
                checked={checked.userAgree}
                onChange={AgreementCheckHandler}
                inputProps={{ 'aria-label': 'controlled' }}
                name="userAgree"
                disabled={!hasReadAgreement}
              />}
              label={<Box component="span" fontSize={14}>I am <Box component="span" fontWeight={500}>{profile.First} {profile.Last}</Box> or thier legal guardian</Box>}
            />
            <FormControlLabel
              control={
                <Checkbox
                  icon={<CheckBoxOutlineBlankOutlinedIcon />}
                  checked={checked.termsAgree}
                  onChange={AgreementCheckHandler}
                  inputProps={{ 'aria-label': 'controlled' }}
                  name="termsAgree"
                  disabled={!hasReadAgreement}
                />
              }
              label={<Box component="span" fontSize={14}>I agree to the terms of this document</Box>}
            />
          </FormGroup>
        </Grid>
      </Grid>
      <Grid container rowSpacing={{ xs: 3, sm: 3, md: 0 }} justifyContent="center">
        <Grid item xs={10} sm={5} md={4} >
          <Box px={2} className={classes.btnWrapper}>
            <LoadingButton variant="contained" color="primary" disabled={(!checked.termsAgree || !checked.userAgree)} onClick={signDocumentHandler} loading={isLoading || updateApiLoading}>
              Sign And Submit (3/3)
            </LoadingButton>
          </Box>

        </Grid>
        <Grid item xs={10} sm={5} md={4} >
          <Box px={2} className={classes.btnWrapper}>
            <Button variant="contained" color="secondary" onClick={onBackHandler}>
              Back
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Container >

  );
};
